import { Log } from '@/appinsights/logging/log';
import store from '@/store/store';
import idleTimeout from 'idle-timeout';

/***** private methods *****************/
/**
 * @returns true if open in App, else false
 */
const isApp_ = (): boolean => {
	if (location.href.includes('client=app')) {
		return true;
	}

	if ((<any>window).nsWebViewBridge !== undefined) {
		return true;
	}
	return false;
};

const isDevelop_ = () => {
	return location.href.startsWith('https://localhost') || location.href.startsWith('https://sit-');
};

const isLocalhost_ = () => {
	return location.href.startsWith('https://localhost');
};

/***** public *****************/
export const isExternalLink = (routePath): boolean => {
	return routePath && (routePath.startsWith('//') || routePath.toLocaleLowerCase().startsWith('https'));
};

/**
 * Sends a message to the app via gotMessage
 * @param message An object containing the message: { 'MAIN_TITLE': 'title'}
 *
 */
export const sendMessage = (message: {}) => {
	if (isApp) {
		(<any>window).nsWebViewBridge?.emit('gotMessage', message);
		if (!(<any>window).nsWebViewBridge) {
			Log.information('App_message', message);
		}
	}
};

export const browserIe11OrOlder = () => {
	if (navigator.userAgent.includes('MSIE') || navigator.appVersion.includes('Trident/')) {
		return true;
	}
	return false;
};

export const handleIdleLogout = (logoutLocation: string, timeInMinutes?: number) => {
	let time = 1000 * 60 * 20;
	if (timeInMinutes) {
		time = 1000 * 60 * timeInMinutes;
	}
	idleTimeout(
		() => {
			location.href = logoutLocation;
		},
		{
			timeout: time,
			loop: true,
			element: <any>document,
		}
	);
};

/* eslint no-empty: ["error", { "allowEmptyCatch": true }] */
export const setPageMetaData = (model: any) => {
	try {
		if (!model) {
			return;
		}
		document.title = model.metaTitle ?? document.title;

		const metaData = [
			{ type: 'description', value: model.metaDescription ?? '', property: 'name' },
			{ type: 'keywords', value: model.metaKeywords ?? '', property: 'name' },
			{
				type: 'og:image',
				value: model.ogImage?.url ?? '',
				property: 'property',
			},
			{ type: 'og:description', value: model.ogDescription ?? '', property: 'property' },
			{ type: 'og:title', value: model.ogTitle ?? '', property: 'property' },
		];
		metaData.forEach((meta) => {
			const metaTag = document.querySelector(`meta[${meta.property}="${meta.type}"]`);

			if (metaTag) {
				metaTag.setAttribute('content', meta.value);
			} else {
				const tag = document.createElement('meta');
				tag.setAttribute(meta.property, meta.type);
				tag.setAttribute('content', meta.value);
				document.head.appendChild(tag);
			}
		});
	} catch (e) {
		console.error('Error setting metadata', e);
		// Too bad
	}
};

export const setVersion = () => {
	try {
		if (isLocalHost) {
			store.state.uiVersion = 'Version 0.23.0_local';
			return;
		}
		const scripts = document.scripts;
		let stop = false;
		for (const item of scripts) {
			if (!stop && item.src.includes('js/app.js') && item.src.includes(location.host)) {
				const src = item.src.split('/');
				src.forEach((element) => {
					if (!Number.isNaN(Number.parseFloat(element))) {
						store.state.uiVersion = 'Version ' + element;
						stop = true;
					}
				});
			}
		}
	} catch (e) {
		console.error('Error setting version', e);
		// Too bad
	}
};

export const getDomain = () => {
	const theme = store.getters.getThemeContext?.toLocaleLowerCase();
	switch (theme) {
		case 'codan':
		case 'codan-theme':
			return { url: 'https://codan.dk', title: 'Til Codan forside' };
		case 'almbrand':
		case 'almbrand-theme':
			return { url: 'https://almbrand.dk', title: 'Til Alm. Brand forside' };
		case 'privatsikring':
		case 'privatsikring-theme':
			return { url: 'https://privatsikring.dk', title: 'Til Privatsikring forside' };
		default:
			return { url: 'https://codan.dk', title: 'Til Codan forside' }; // Default to Codan
	}
};

export const getBrand = (): string => {
	const theme = store.getters.getThemeContext?.toLocaleLowerCase();
	switch (theme) {
		case 'codan':
		case 'codan-theme':
			return 'Codan';
		case 'almbrand':
		case 'almbrand-theme':
			return 'Alm. Brand';
		case 'privatsikring':
		case 'privatsikring-theme':
			return 'Privatsikring';
		default:
			return 'ABG Group'; // Default to ABG group
	}
};

export const getBrandForConsent = (): string => {
	const brand = getBrand();
	if (brand === 'Privatsikring') {
		return 'psk';
	}
	if (brand === 'Codan') {
		return 'codan';
	}
	return brand;
};

export const getQueryParameter = (name = ''): string | undefined => {
	const urlParams = new URLSearchParams(location.search);
	const nameLower = name.toLowerCase().trim();
	for (const [key, value] of urlParams.entries()) {
		if (key.toLowerCase().trim() === nameLower) {
			return value;
		}
	}
};

export const isApp = isApp_();
export const isWeb = !isApp;
export const isDevelop = isDevelop_();
export const isLocalHost = isLocalhost_();
